import { DateTime } from "luxon";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { SubscriptionGroupMemberMyleDto, SubscriptionGroupMyleDto } from "@/Common/Models/Portal/MedfarPortal";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { LinkMylePatientToPortalAccountDialog } from "./LinkMylePatientToPortalAccountDialog";

interface ISubscriptionUserProps {
    subscription: SubscriptionGroupMyleDto;
    patientContactId: string;
    areControlsDisabled?: boolean;
}

class SubscriptionUserManagementState {
    public isSubscriptionAddAccountPopupVisible: boolean = false;
}

class SubscriptionUserManagement extends React.Component<ISubscriptionUserProps, SubscriptionUserManagementState> {
    constructor(props: ISubscriptionUserProps) {
        super(props);
        this.state = new SubscriptionUserManagementState();
    }

    public componentDidUpdate(prevProps: ISubscriptionUserProps): void {
        if (this.props.subscription !== prevProps.subscription) {
            this.setState({
                isSubscriptionAddAccountPopupVisible: false
            });
        }
    }

    public render(): JSX.Element {
        let self = this;
        let subscription = this.props.subscription;
        let hasSubscription = subscription != null;
        let result: JSX.Element = null;

        let className = "psingle-column centered";
        if (this.props.areControlsDisabled)
            className = "";

        result = (
            <div>
                {this.state.isSubscriptionAddAccountPopupVisible && (
                    <LinkMylePatientToPortalAccountDialog
                        patientContactId={self.props.patientContactId}
                        componentId="patientPortal_sub_linkMemberAccount"
                        onCloseCallback={self.toggleDisplayAddPatientToSubscriptionPopup.bind(self)}
                        onAddFunction={self.onAttachUser.bind(self)}
                    />
                )}

                <div className="block separated">
                    <div className="flex-container heading">
                        <h3 className="thin-font"> {cacheStore.GetString("SubscriptionLabel")}: {cacheStore.GetString("AccountManagementLabel")}</h3>
                        {!this.props.areControlsDisabled && (
                            <button disabled={!hasSubscription} onClick={self.toggleDisplayAddPatientToSubscriptionPopup.bind(self)} className="btn btn-primary">
                                <span>{cacheStore.GetString("AddLabel")}</span>
                            </button>
                        )}

                    </div>
                    <div className={className}>
                        {!hasSubscription &&
                            <div style={{ paddingBottom: "10px" }} className="center"> {cacheStore.GetString("NoSubscriptionLabel")} </div>
                        }

                        {!this.props.areControlsDisabled && hasSubscription && subscription.Members != null && (
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>{cacheStore.GetString("NameLabel")}</th>
                                        <th>{cacheStore.GetString("DateofBirthLabel")}</th>
                                        <th>{cacheStore.GetString("RoleLabel")}</th>
                                        <th className="center">{cacheStore.GetString("ActionLabel")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscription.Members.map((member: SubscriptionGroupMemberMyleDto, idx) => (
                                        <tr key={"__sub-member-" + idx}>
                                            <td>
                                                <HyperLinkRedirectControl
                                                    target={RedirectTo.Patient}
                                                    redirectValue={member.MyleId}
                                                    text={NameHelper.formatFullNameForHasName(member, NameHelperFormat.FirstNameFirst)}
                                                />
                                            </td>
                                            <td>{DateTime.fromISO(member.DateOfBirth, { setZone: true }).toFormat("yyyy-LL-dd")}</td>
                                            {member.IsManager &&
                                                <td> <span>{cacheStore.GetString("ManagerLabel")}</span> </td>
                                            }
                                            {member.IsManager === false &&
                                                <td> <span>{cacheStore.GetString("UserLabel")}</span> </td>
                                            }
                                            <td className="center">
                                                {member.IsManager === false &&
                                                    <span onClick={self.displayRemovePatientFromSubscriptionPopup.bind(self, member)} className="red-x"> X </span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {this.props.areControlsDisabled && hasSubscription && subscription.Members != null && (
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>{cacheStore.GetString("NameLabel")}</th>
                                        <th>{cacheStore.GetString("RoleLabel")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscription.Members.map((member: SubscriptionGroupMemberMyleDto, idx) => (
                                        <tr key={"__sub-member-" + idx}>
                                            <td>
                                                <strong>
                                                    {NameHelper.formatFullNameForHasName(member, NameHelperFormat.FirstNameFirst)}
                                                </strong>
                                            </td>
                                            {member.IsManager &&
                                                <td> <span>{cacheStore.GetString("ManagerLabel")}</span> </td>
                                            }
                                            {member.IsManager === false &&
                                                <td> <span>{cacheStore.GetString("UserLabel")}</span> </td>
                                            }
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );

        return result;
    }

    private toggleDisplayAddPatientToSubscriptionPopup(): void {
        let s = new SubscriptionUserManagementState();
        s.isSubscriptionAddAccountPopupVisible = !this.state.isSubscriptionAddAccountPopupVisible;
        this.setState(s);
    }

    private displayRemovePatientFromSubscriptionPopup(member: SubscriptionGroupMemberMyleDto): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("ConfirmRemovalLabel"),
            content: cacheStore.GetString("RemoveMyleAccountFromSubscriptionMembersLabel"),

            okLabel: cacheStore.GetString("DeleteLabel"),
            okAction: this.removePatientFromSubscription.bind(this, member)
        });
    }

    private removePatientFromSubscription(member: SubscriptionGroupMemberMyleDto): void {
        let self = this;
        let requestData = {
            PatientId: self.props.patientContactId,
            AddedMemberMyleId: member.MyleId,
            AddedMemberPortalId: member.PatientProfileId,
            IsRemove: true,
            SuscriptionId: self.props.subscription.SubscriptionGroupId
        };

        let client = new Client();
        client.Post(ServerOperations.Server.Portal_Proxy_Subscription_ChangeManagedAccounts, requestData, response => self.onRemoveSuccess(response), () => {});
    }

    private onRemoveSuccess(response: ServerEvent): void {
        if (response.Errors && response.Errors.length)
            sessionStore.showPopup(response.Errors);
        else {
            const op = new LocalEvent(LocalOperations.Portal.Subscription_Load);
            op.AddItem("patientContactId", this.props.patientContactId);
            op.AddItem("subscriptionId", this.props.subscription.SubscriptionGroupId);
            LocalEventDispatcher.dispatch(op);
        }
    }

    private onAttachUser(newMemberMyleId: string, onErrorFunction: (error: any) => void): void {
        let self = this;
        let requestData = {
            PatientId: self.props.patientContactId,
            AddedMemberMyleId: newMemberMyleId,
            IsRemove: false,
            SuscriptionId: self.props.subscription.SubscriptionGroupId
        };

        let client = new Client();
        client.Post(ServerOperations.Server.Portal_Proxy_Subscription_ChangeManagedAccounts, requestData, response => self.onAddSuccess(response), err => onErrorFunction(err));
    }

    private onAddSuccess(response: ServerEvent): void {
        let errors = response.Errors as ErrorMessage[];

        if (errors == null || errors.length == 0) {
            const op = new LocalEvent(LocalOperations.Portal.Subscription_Load);
            op.AddItem("patientContactId", this.props.patientContactId);
            op.AddItem("subscriptionId", this.props.subscription.SubscriptionGroupId);
            LocalEventDispatcher.dispatch(op);

            this.toggleDisplayAddPatientToSubscriptionPopup();
        }
    }
}

export { SubscriptionUserManagement };
